import { Injectable, inject } from '@angular/core';
import { format } from 'date-fns';
import { defaultLoggerOptions, LOGGER_PROVIDERS, LoggerOptions, MIN_LOG_LEVEL } from '../logger-options';

import { LoggerConfigService } from './logger-config.service';
import { LogLevel, LogMethod } from '../log-levels';
import { LoggerProvider } from './logger-provider.service';

/**
 * Vzor názvu modulu z `moduleLogLevels` (`*` = libovolný text, `?` = jeden znak, např. `*Component`, `Shared*Service`)
 * jako RegExp. Náhrada knihovny minimatch, která do prohlížeče tahala zbytečný kód.
 */
export function modulePatternToRegExp(pattern: string): RegExp {
  const source = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '[^/]*')
    .replace(/\?/g, '[^/]');
  return new RegExp(`^${source}$`);
}

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly options: LoggerOptions;
  private readonly minLogLevel: LogLevel;
  private readonly modulePatterns: ReadonlyArray<{ readonly regex: RegExp; readonly level: LogLevel }>;
  private loggerConfigService: LoggerConfigService;
  public readonly providers: LoggerProvider[];

  constructor() {
    this.loggerConfigService = inject(LoggerConfigService);
    const injectedOptions = this.loggerConfigService.getLoggerOptions();
    this.options = { ...defaultLoggerOptions, ...injectedOptions };
    this.minLogLevel = inject(MIN_LOG_LEVEL) ?? this.options.minLogLevel ?? LogLevel.NEVER;
    this.modulePatterns = Object.entries(this.options.moduleLogLevels ?? {}).map(([pattern, level]) => ({
      regex: modulePatternToRegExp(pattern),
      level,
    }));
    this.providers = inject(LOGGER_PROVIDERS) ?? [];

    // Předání sloučených options do každého provideru
    this.providers.forEach((provider) => provider.setOptions(this.options));
  }
  canLog(logLevel: LogLevel, moduleName?: string): boolean {
    if (!moduleName) {
      return logLevel >= this.minLogLevel;
    }

    let applicableLogLevel: LogLevel | undefined = undefined;

    for (const { regex, level } of this.modulePatterns) {
      if (regex.test(moduleName) && (applicableLogLevel === undefined || level < applicableLogLevel)) {
        applicableLogLevel = level;
      }
    }

    return logLevel >= (applicableLogLevel ?? this.minLogLevel);
  }

  createLogger(moduleName: string): Logger {
    return new Logger(this, moduleName, this.options);
  }
}

export class Logger {
  constructor(
    private loggerService: LoggerService,
    private moduleName: string,
    private options: LoggerOptions
  ) {}

  private formatLog(template: string): string {
    const dateFormat = this.options.dateFormat ?? 'HH:mm:ss';
    const dateStr = format(new Date(), dateFormat);
    const logFormat = this.options.logFormat ?? '[{date}] [{module}] {message}';
    return logFormat.replace('{date}', dateStr).replace('{module}', this.moduleName).replace('{message}', template);
  }

  private log(method: LogMethod, logLevel: LogLevel, template: string, ...optionalParams: unknown[]): void {
    if (!this.loggerService.canLog(logLevel, this.moduleName)) return;

    const formattedMessage = this.formatLog(template);
    this.loggerService.providers.forEach((provider) => {
      provider[method](formattedMessage, ...optionalParams);
    });
  }

  debug(template: string, ...optionalParams: unknown[]): void {
    this.log(LogMethod.DEBUG, LogLevel.DEBUG, template, ...optionalParams);
  }

  info(template: string, ...optionalParams: unknown[]): void {
    this.log(LogMethod.INFO, LogLevel.INFO, template, ...optionalParams);
  }

  warn(template: string, ...optionalParams: unknown[]): void {
    this.log(LogMethod.WARN, LogLevel.WARNING, template, ...optionalParams);
  }

  error(template: string, ...optionalParams: unknown[]): void {
    this.log(LogMethod.ERROR, LogLevel.ERROR, template, ...optionalParams);
  }

  table(data: unknown, columns?: string[]): void {
    this.loggerService.providers.forEach((provider) => {
      provider.table(data, columns);
    });
  }

  // New methods for checking if a specific log level is enabled
  isErrorEnabled(): boolean {
    return this.loggerService.canLog(LogLevel.ERROR, this.moduleName);
  }

  isWarningEnabled(): boolean {
    return this.loggerService.canLog(LogLevel.WARNING, this.moduleName);
  }

  isInfoEnabled(): boolean {
    return this.loggerService.canLog(LogLevel.INFO, this.moduleName);
  }

  isDebugEnabled(): boolean {
    return this.loggerService.canLog(LogLevel.DEBUG, this.moduleName);
  }

  isTraceEnabled(): boolean {
    return this.loggerService.canLog(LogLevel.TRACE, this.moduleName);
  }
}
